var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// components/shared/common/src/index.ts
import { Deferred } from "gs-uitk-promise-utils";
import {
  detectIndentStr,
  formatTextBlock,
  formatTextBlockTpl,
  removeLeadingIndent
} from "gs-uitk-text-utils";

// components/shared/common/src/utils/getRandomNumber.ts
function getRandomNumber(limit) {
  if (typeof limit === "undefined")
    limit = 1e3;
  return Math.floor(Math.random() * limit);
}

// components/shared/common/src/utils/getUniqueNumber.ts
function getUniqueNumber(set, limit) {
  let curr = getRandomNumber(limit);
  while (set.indexOf(curr) > -1) {
    curr = getRandomNumber(limit);
  }
  return curr;
}

// components/shared/common/src/utils/getGradientOverlay.ts
import { rgba, linearGradient } from "polished";
function getGradientOverlay(color, degree) {
  return linearGradient({
    toDirection: "0deg",
    colorStops: [rgba(color, degree), rgba(color, degree)]
  }).backgroundImage;
}

// components/shared/common/src/utils/text-overflow-utils.ts
function getOverflowStyles(overflow) {
  switch (overflow) {
    case "wrap":
      return {
        overflowWrap: "break-word",
        // IE11 compatibility
        wordBreak: "break-word",
        // Enables the break-word text wrapping within flex (must be 1px for IE11)
        // Ref: https://stackoverflow.com/questions/36150458/flex-item-overflows-container-due-to-long-word-even-after-using-word-wrap/36150638
        minWidth: "1px"
      };
    case "truncate":
      return {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      };
    default:
      return {};
  }
}

// components/shared/common/src/utils/key-code-helpers.ts
var KeyHelpers = {};
var i;
KeyHelpers.isPrintableChar = (keyCode) => (
  //special chars (229 for Asian chars)
  keyCode === 32 || //space
  keyCode >= 48 && keyCode <= 57 || //0-9
  keyCode >= 96 && keyCode <= 111 || //numpad
  keyCode >= 186 && keyCode <= 192 || //;=,-./`
  keyCode >= 219 && keyCode <= 222 || //[]{}\|"'
  keyCode >= 226 || keyCode >= 65 && keyCode <= 90
);
KeyHelpers.fromKeyCode = (keyCode, shiftKey) => {
  let value;
  if (keyCode >= 48 && keyCode <= 57) {
    value = keyCode - 48;
  } else if (keyCode >= 96 && keyCode <= 105) {
    value = keyCode - 96;
  } else if (keyCode === 110 || keyCode === 190) {
    value = ".";
  } else if (keyCode === 109 || keyCode === 189) {
    value = "-";
  } else if (keyCode === 187) {
    value = shiftKey ? "+" : "=";
  } else if (keyCode >= 65 && keyCode <= 90) {
    value = String.fromCharCode(keyCode);
    value = shiftKey ? value.toUpperCase() : value.toLowerCase();
  }
  return value === void 0 ? "" : value;
};
KeyHelpers.alphabet = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z"
];
KeyHelpers.isMetaKey = (keyCode) => {
  const keyCodes = KeyHelpers.keyCode;
  const metaKeys = [
    keyCodes.ARROW_DOWN,
    keyCodes.ARROW_UP,
    keyCodes.ARROW_LEFT,
    keyCodes.ARROW_RIGHT,
    keyCodes.HOME,
    keyCodes.END,
    keyCodes.DELETE,
    keyCodes.BACKSPACE,
    keyCodes.F1,
    keyCodes.F2,
    keyCodes.F3,
    keyCodes.F4,
    keyCodes.F5,
    keyCodes.F6,
    keyCodes.F7,
    keyCodes.F8,
    keyCodes.F9,
    keyCodes.F10,
    keyCodes.F11,
    keyCodes.F12,
    keyCodes.TAB,
    keyCodes.PAGE_DOWN,
    keyCodes.PAGE_UP,
    keyCodes.ENTER,
    keyCodes.ESCAPE,
    keyCodes.SHIFT,
    keyCodes.CAPS_LOCK,
    keyCodes.ALT
  ];
  return metaKeys.indexOf(keyCode) !== -1;
};
KeyHelpers.isCtrlKey = (keyCode) => {
  const keys = KeyHelpers.keyCode;
  return [keys.CONTROL_LEFT, 224, keys.COMMAND_LEFT, keys.COMMAND_RIGHT].indexOf(keyCode) !== -1;
};
KeyHelpers.keyCode = {
  MOUSE_LEFT: 1,
  MOUSE_RIGHT: 3,
  MOUSE_MIDDLE: 2,
  BACKSPACE: 8,
  AMPERSAND: 55,
  "&": 55,
  INSERT: 45,
  DELETE: 46,
  END: 35,
  ENTER: 13,
  ESCAPE: 27,
  CONTROL_LEFT: 91,
  COMMAND_LEFT: 17,
  COMMAND_RIGHT: 93,
  CTRL: 17,
  ALT: 18,
  HOME: 36,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  PERIOD: 190,
  SPACE: 32,
  " ": 32,
  SHIFT: 16,
  CAPS_LOCK: 20,
  TAB: 9,
  ARROW_RIGHT: 39,
  ARROW_LEFT: 37,
  ARROW_UP: 38,
  ARROW_DOWN: 40,
  F1: 112,
  F2: 113,
  F3: 114,
  F4: 115,
  F5: 116,
  F6: 117,
  F7: 118,
  F8: 119,
  F9: 120,
  F10: 121,
  F11: 122,
  F12: 123,
  A: 65,
  X: 88,
  C: 67,
  V: 86,
  EQUALS: 187,
  COMMA: 188,
  ",": 188,
  QUOTATION_MARK: 222,
  '"': 222,
  APOSTROPHE: 222,
  "'": 222
};
for (i = 97; i < 123; i++) {
  KeyHelpers.keyCode[String.fromCharCode(i)] = i;
  KeyHelpers.keyCode[String.fromCharCode(i).toUpperCase()] = i - 32;
}
for (i = 48; i < 58; i++) {
  KeyHelpers.keyCode[i - 48] = i;
}

// components/shared/common/src/utils/logger.ts
var LOG_LEVELS = ["error", "warn", "info", "debug"];
function debug(...args) {
  if (globalLogLevelIsAtLeast("debug")) {
    console.log(...args);
  }
}
function info(...args) {
  if (globalLogLevelIsAtLeast("info")) {
    console.log(...args);
  }
}
function warn(...args) {
  if (globalLogLevelIsAtLeast("warn")) {
    console.warn(...args);
  }
}
function error(...args) {
  if (globalLogLevelIsAtLeast("error")) {
    console.error(...args);
  }
}
var Logger = { debug, info, warn, error };
function globalLogLevelIsAtLeast(logLevel) {
  let globalLogLevel = "warn";
  const win2 = typeof window === "undefined" ? void 0 : window;
  if (win2 && win2.UITOOLKIT_LOG_LEVEL) {
    globalLogLevel = win2.UITOOLKIT_LOG_LEVEL.toLowerCase();
  }
  return LOG_LEVELS.indexOf(globalLogLevel) >= LOG_LEVELS.indexOf(logLevel);
}

// components/shared/common/src/utils/mixin.ts
var Mixin = class {
  static mix(...mixins) {
    for (const mixin of mixins) {
      this.copyProperties(Mix, mixin);
      this.copyProperties(Mix.prototype, mixin.prototype);
    }
    return Mix;
  }
  static extendAndMix(superclass, ...mixins) {
    for (const mixin of mixins) {
      this.copyProperties(superclass, mixin);
      this.copyProperties(superclass.prototype, mixin.prototype);
    }
    return superclass;
  }
  static copyProperties(target, ...sources) {
    for (const source of sources) {
      if (source) {
        for (const key of Reflect.ownKeys(source)) {
          if (!["constructor", "prototype", "name"].includes(key)) {
            const desc = Object.getOwnPropertyDescriptor(source, key);
            Object.defineProperty(target, key, desc);
          }
        }
      }
    }
  }
};
var Mix = class {
};

// components/shared/common/src/utils/resize-observer.ts
var UiToolkitResizeObserver = class _UiToolkitResizeObserver {
  constructor(target, callback) {
    this.callback = callback;
    this.observe(target);
  }
  observe(target) {
    this.resizeObserver = this.resizeObserver || _UiToolkitResizeObserver.create(this.callback);
    if (this.resizeObserver) {
      this.resizeObserver.observe(target);
    }
  }
  unobserve(target = null) {
    if (this.resizeObserver) {
      if (target) {
        this.resizeObserver.unobserve(target);
      } else {
        this.resizeObserver.disconnect();
      }
    }
  }
  static create(callback) {
    const win2 = window;
    if (win2.ResizeObserver) {
      return new win2.ResizeObserver(callback);
    }
    Logger.warn(
      [
        "Warning: Components of this application depend upon ResizeObserver, which is undefined in this browser.",
        "If you are the application developer, please install a polyfill:",
        "  jsb npm i -S resize-observer-polyfill",
        "Import the polyfill into your application:",
        "  import ResizeObserver from 'resize-observer-polyfill';",
        "  window.ResizeObserver = window.ResizeObserver || ResizeObserver;"
      ].join("\n")
    );
    return null;
  }
};

// components/shared/common/src/utils/throttle.ts
function throttle(fn, wait) {
  let args;
  let result;
  let timeout = null;
  let previous = 0;
  const later = () => {
    previous = Date.now();
    timeout = null;
    result = fn.apply(this, args);
    if (!timeout) {
      args = null;
    }
  };
  return (...fnArgs) => {
    const now = Date.now();
    const remaining = wait - (now - previous);
    args = fnArgs;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        window.clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = fn.apply(this, args);
      if (!timeout) {
        args = null;
      }
    } else if (!timeout) {
      timeout = window.setTimeout(later, remaining);
    }
    return result;
  };
}

// components/shared/common/src/utils/get-global-this.ts
function getGlobalThis() {
  if (typeof globalThis !== "undefined")
    return globalThis;
  if (typeof self !== "undefined")
    return self;
  if (typeof window !== "undefined")
    return window;
  if (typeof global !== "undefined")
    return global;
  throw new Error("Unable to locate global `this`");
}

// components/shared/common/src/utils/array-utils.ts
var arraySetsEqual = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  b.forEach((el) => set.delete(el));
  return set.size === 0;
};

// components/shared/common/src/utils/browser-utils.ts
var isBrowser = typeof window !== "undefined";
var isIE = typeof window === "undefined" ? false : !!window.document.documentMode;
var isEdge = typeof window === "undefined" ? false : !isIE && !!window.StyleMedia;
var isFirefox = typeof InstallTrigger !== "undefined";
var isSafari = typeof window === "undefined" ? false : /constructor/i.test(window.HTMLElement) || function(p) {
  return p.toString() === "[object SafariRemoteNotification]";
}(
  !window["safari"] || typeof safari !== "undefined" && window["safari"].pushNotification
);
var isTestEnv = () => {
  return typeof jasmine !== "undefined" || typeof jest !== "undefined" || typeof mocha !== "undefined";
};
var isTouchDevice = () => typeof window !== "undefined" && // for SSR environments
("ontouchstart" in window || window.DocumentTouch && document instanceof window.DocumentTouch || navigator.maxTouchPoints > 0);

// components/shared/common/src/utils/element-utils.ts
function setAttributes(element, attributes) {
  for (const key in attributes) {
    element.setAttribute(key, attributes[key]);
  }
}

// components/shared/common/src/utils/hex-to-rgb.ts
var hexToRgb = (hex, alpha) => {
  if (hex.length !== 4 && hex.length !== 7) {
    return "";
  }
  const sectionLength = hex.length === 4 ? 1 : 2;
  let index = 1;
  const rString = hex.slice(index, index + sectionLength);
  const r = parseInt(rString + (sectionLength === 1 ? rString : ""), 16);
  index += sectionLength;
  const gString = hex.slice(index, index + sectionLength);
  const g = parseInt(gString + (sectionLength === 1 ? gString : ""), 16);
  index += sectionLength;
  const bString = hex.slice(index, index + sectionLength);
  const b = parseInt(bString + (sectionLength === 1 ? bString : ""), 16);
  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return "rgb(" + r + ", " + g + ", " + b + ")";
  }
};

// components/shared/common/src/utils/window.ts
var win = typeof window === "undefined" ? void 0 : window;

// components/shared/common/src/utils/toolkit-version-checker.ts
var GS_UX_UITOOLKIT_VERSIONS = "GS_UX_UITOOLKIT_VERSIONS";
var VERSION_CHECK_SKIPPED_PACKAGES = [
  "@gs-ux-uitoolkit-react/d3-chart",
  "@gs-ux-uitoolkit-common/d3-chart",
  "@gs-ux-uitoolkit-angular/d3-chart",
  "@gs-ux-uitoolkit-react/graph",
  "@gs-ux-uitoolkit-common/graph",
  "@gs-ux-uitoolkit-angular/graph"
];
function trackComponentVersion(componentName, componentVersion) {
  const versions = getComponentVersionTracker();
  versions.components[componentName] = componentVersion;
  if (!versions.firstRegisteredComponentVersion) {
    versions.firstRegisteredComponentVersion = componentVersion;
    versions.firstRegisteredComponentName = componentName;
  } else {
    if (versions.firstRegisteredComponentVersion !== componentVersion && getGlobalThis2().GS_UX_UITOOLKIT_VERSIONS_CHECK_DISABLE !== "hide-warnings") {
      if (getGlobalThis2().GS_UX_UITOOLKIT_VERSIONS_CHECK_DISABLE === true || VERSION_CHECK_SKIPPED_PACKAGES.indexOf(componentName) > -1) {
        Logger.info(
          `GS_UX_UITOOLKIT_VERSIONS_CHECK_DISABLE global has been set 'true'. Mixed versions of UI Toolkit is allowed.
UI Toolkit package versions registered: `,
          versions
        );
      } else {
        Logger.error(
          "UI Toolkit versions registered: ",
          versions,
          "\n",
          `${componentName}: ${componentVersion} is different from the first registered toolkit component version: ${versions.firstRegisteredComponentName}: ${versions.firstRegisteredComponentVersion}
`,
          "\n",
          `For more information, refer to error 'mixed-ui-toolkit-versions' on https://ui.web.gs.com/docs/react/error-descriptions`
        );
      }
    }
  }
}
function getComponentVersionTracker() {
  const globalThis2 = getGlobalThis2();
  if (!globalThis2[GS_UX_UITOOLKIT_VERSIONS]) {
    const newVersionTracker = {
      firstRegisteredComponentName: void 0,
      firstRegisteredComponentVersion: void 0,
      components: {}
    };
    globalThis2[GS_UX_UITOOLKIT_VERSIONS] = newVersionTracker;
  }
  return globalThis2[GS_UX_UITOOLKIT_VERSIONS];
}
function getGlobalThis2() {
  if (typeof globalThis !== "undefined")
    return globalThis;
  if (typeof global !== "undefined")
    return global;
  if (typeof window !== "undefined")
    return window;
  throw new Error("Unable to locate global `this`");
}

// components/shared/common/src/utils/object-utils.ts
function defaults(object, ...sources) {
  for (const source of sources) {
    for (const prop in source) {
      if (object[prop] === void 0) {
        object[prop] = source[prop];
      }
    }
  }
  return object;
}

// components/shared/common/src/utils/query-string.ts
var parseQueryStringParams = (query) => {
  return query ? (/^[?#]/.test(query) ? query.slice(1) : query).split("&").reduce((params, param) => {
    const [key, value] = param.split("=");
    params[key] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
    return params;
  }, {}) : {};
};

// components/shared/common/src/utils/string-utils.ts
function startsWith(targetString, searchString) {
  return targetString.slice(0, searchString.length) === searchString;
}
function upperFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function recombineTaggedTemplateStr(strings, args) {
  const strArr = [];
  for (let i2 = 0, len = strings.length, argsLen = args.length; i2 < len; i2++) {
    strArr.push(strings[i2]);
    if (i2 < argsLen)
      strArr.push(args[i2]);
  }
  return strArr.join("");
}
var upperCaseLetterRe = /[A-Z]/;
var lowerCaseLetterRe = /[a-z]/;
var digitRe = /\d/;
function kebabCase(str) {
  if (str.length === 0)
    return str;
  let result = "";
  let state = 0 /* Start */;
  for (let i2 = 0, len = str.length; i2 < len; i2++) {
    const char = str.charAt(i2);
    switch (state) {
      case 0 /* Start */:
        handleStateStart(char);
        break;
      case 2 /* LowerCaseLetter */:
        handleStateLowerCaseLetter(char);
        break;
      case 1 /* UpperCaseLetter */:
        handleStateUpperCaseLetter(char);
        break;
      case 3 /* Digit */:
        handleStateDigit(char);
        break;
      case 4 /* Dash */:
        handleStateDash(char);
        break;
    }
  }
  if (result.charAt(result.length - 1) === "-") {
    result = result.slice(0, result.length - 1);
  }
  return result;
  function handleStateStart(char) {
    if (lowerCaseLetterRe.test(char)) {
      result = char;
      state = 2 /* LowerCaseLetter */;
    } else if (upperCaseLetterRe.test(char)) {
      result = char.toLowerCase();
      state = 1 /* UpperCaseLetter */;
    } else if (digitRe.test(char)) {
      result = char;
      state = 3 /* Digit */;
    }
  }
  function handleStateLowerCaseLetter(char) {
    if (lowerCaseLetterRe.test(char)) {
      result += char;
    } else if (upperCaseLetterRe.test(char)) {
      result += "-" + char.toLowerCase();
      state = 1 /* UpperCaseLetter */;
    } else if (digitRe.test(char)) {
      result += "-" + char;
      state = 3 /* Digit */;
    } else {
      result += "-";
      state = 4 /* Dash */;
    }
  }
  function handleStateUpperCaseLetter(char) {
    if (lowerCaseLetterRe.test(char)) {
      result += char;
      state = 2 /* LowerCaseLetter */;
    } else if (upperCaseLetterRe.test(char)) {
      result += char.toLowerCase();
    } else if (digitRe.test(char)) {
      result += "-" + char;
      state = 3 /* Digit */;
    } else {
      result += "-";
      state = 4 /* Dash */;
    }
  }
  function handleStateDigit(char) {
    if (lowerCaseLetterRe.test(char)) {
      result += "-" + char;
      state = 2 /* LowerCaseLetter */;
    } else if (upperCaseLetterRe.test(char)) {
      result += "-" + char.toLowerCase();
      state = 1 /* UpperCaseLetter */;
    } else if (digitRe.test(char)) {
      result += char;
    } else {
      result += "-";
      state = 4 /* Dash */;
    }
  }
  function handleStateDash(char) {
    if (lowerCaseLetterRe.test(char)) {
      result += char;
      state = 2 /* LowerCaseLetter */;
    } else if (upperCaseLetterRe.test(char)) {
      result += char.toLowerCase();
      state = 1 /* UpperCaseLetter */;
    } else if (digitRe.test(char)) {
      result += char;
      state = 3 /* Digit */;
    }
  }
}
function findStringSimilarityScore(first, second) {
  first = first.replace(/\s+/g, "");
  second = second.replace(/\s+/g, "");
  if (first === second)
    return 1;
  if (first.length < 2 || second.length < 2)
    return 0;
  const firstBigrams = /* @__PURE__ */ new Map();
  for (let i2 = 0; i2 < first.length - 1; i2++) {
    const bigram = first.substring(i2, i2 + 2);
    const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) + 1 : 1;
    firstBigrams.set(bigram, count);
  }
  let intersectionSize = 0;
  for (let i2 = 0; i2 < second.length - 1; i2++) {
    const bigram = second.substring(i2, i2 + 2);
    const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) : 0;
    if (count > 0) {
      firstBigrams.set(bigram, count - 1);
      intersectionSize++;
    }
  }
  return 2 * intersectionSize / (first.length + second.length - 2);
}
function findBestStringMatch(mainString, stringsToMatch) {
  const ratings = [];
  let bestMatchIndex = 0;
  for (let i2 = 0; i2 < stringsToMatch.length; i2++) {
    const currentTargetString = stringsToMatch[i2];
    const currentRating = findStringSimilarityScore(mainString, currentTargetString);
    ratings.push({ target: currentTargetString, rating: currentRating });
    if (currentRating > ratings[bestMatchIndex].rating) {
      bestMatchIndex = i2;
    }
  }
  return ratings[bestMatchIndex].target;
}

// components/shared/common/src/utils/reflow.ts
function reflow(element) {
  return (element || document.body).offsetHeight;
}

// components/shared/common/src/utils/assert-never.ts
function assertNever(val) {
  throw new Error(`Unexpected object in exhaustive check: ${val}`);
}

// components/shared/common/src/utils/copy-to-clipboard.ts
function copyToClipboard(text) {
  if (!navigator.clipboard) {
    fallbackCopyToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).catch((e) => {
    console.error(`Oops, unable to copy ${text}`, e);
  });
}
function fallbackCopyToClipboard(text) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand("copy");
  } catch (e) {
    console.error(`Oops, unable to copy ${text}`, e);
  }
  document.body.removeChild(textArea);
}

// components/shared/common/src/utils/injectable.ts
function Injectable() {
  return (target) => target;
}

// components/shared/common/src/utils/inside-outside-event-listener.ts
var InsideOutsideEventListener = class {
  /**
   * @param elements The element(s) to check events for.
   * @param eventNames The event name(s) to monitor.
   * @param handlers The callback functions for the event happening either inside the element(s),
   *   or outside the element(s)
   */
  constructor(elements, eventNames, handlers) {
    /**
     * Handles a subscribed event by checking if the event fell within an element inside of the
     * given {@link #elements} or outside of them.
     */
    this.handleEvent = (evt) => {
      const elements = this.elements;
      for (let i2 = 0, len = elements.length; i2 < len; i2++) {
        if (elements[i2].contains(evt.target)) {
          this.onInsideHandler(evt);
          return;
        }
      }
      this.onOutsideHandler(evt);
    };
    this.elements = [].concat(elements);
    this.eventNames = [].concat(eventNames);
    this.onInsideHandler = handlers.onInside || (() => {
    });
    this.onOutsideHandler = handlers.onOutside || (() => {
    });
    this.subscribeToEvents();
  }
  /**
   * Subscribes to the requested events at the document level in order to capture the event from
   * either inside or outside the {@link #elements}.
   */
  subscribeToEvents() {
    this.eventNames.forEach((evtName) => {
      document.addEventListener(evtName, this.handleEvent);
    });
  }
  /**
   * Cleans up by removing the event listeners.
   */
  remove() {
    this.eventNames.forEach((evtName) => {
      document.removeEventListener(evtName, this.handleEvent);
    });
  }
};

// components/shared/common/src/utils/log-warning-once.ts
var displayedWarnings = {};
function logWarningOnce(warningMessage) {
  if (!displayedWarnings[warningMessage]) {
    console.warn(warningMessage);
    displayedWarnings[warningMessage] = true;
  }
}

// components/shared/common/src/utils/non-nullable-value-or-throw.ts
function nonNullableValueOrThrow(value, errorMessage) {
  if (value == null) {
    throw new Error(errorMessage);
  } else {
    return value;
  }
}

// components/shared/common/src/utils/sleep.ts
function sleep(millis) {
  return __async(this, null, function* () {
    return new Promise((resolve) => {
      setTimeout(resolve, millis);
    });
  });
}

// components/shared/common/src/utils/request-animation-frame.ts
function requestAnimationFrame() {
  return __async(this, null, function* () {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

// components/shared/common/src/utils/sanitize-html.ts
function sanitizeHtml(str) {
  const p = document.createElement("p");
  p.appendChild(document.createTextNode(str));
  return p.innerHTML;
}
function unsanitizeHtml(str) {
  str = str.replace(/&lt;/g, "<");
  str = str.replace(/&gt;/g, ">");
  str = str.replace(/&quot;/g, '"');
  str = str.replace(/&#39;/g, "'");
  str = str.replace(/&amp;/g, "&");
  return str;
}

// components/shared/common/src/events/ui-event.ts
var UiEvent = class {
  constructor({ nativeEvent, type }) {
    this.target = null;
    this.nativeEvent = nativeEvent;
    this.type = type || this.nativeEvent.type;
    this.bubbles = this.nativeEvent.bubbles;
    this.cancelable = this.nativeEvent.cancelable;
    this.isTrusted = this.nativeEvent.isTrusted;
    this.timeStamp = this.nativeEvent.timeStamp;
  }
  get defaultPrevented() {
    return this.nativeEvent.defaultPrevented;
  }
  get eventPhase() {
    return this.nativeEvent.eventPhase;
  }
  get currentTarget() {
    return this.nativeEvent.currentTarget;
  }
  preventDefault() {
    this.nativeEvent.preventDefault();
  }
  stopImmediatePropagation() {
    this.nativeEvent.stopImmediatePropagation();
  }
  stopPropagation() {
    this.nativeEvent.stopPropagation();
  }
};

// components/shared/common/src/events/create-cross-browser-event.ts
var createCrossBrowserEvent = (props) => {
  let event;
  const { type, bubbles = true, cancelable = true } = props;
  if (typeof Event === "function") {
    event = new Event(type, { bubbles, cancelable });
  } else {
    event = document.createEvent("Event");
    event.initEvent(type, bubbles, cancelable);
  }
  return event;
};
export {
  Deferred,
  Injectable,
  InsideOutsideEventListener,
  KeyHelpers,
  Logger,
  Mixin,
  UiEvent,
  UiToolkitResizeObserver,
  arraySetsEqual,
  assertNever,
  copyToClipboard,
  createCrossBrowserEvent,
  defaults,
  detectIndentStr,
  findBestStringMatch,
  formatTextBlock,
  formatTextBlockTpl,
  getGlobalThis,
  getGradientOverlay,
  getOverflowStyles,
  getRandomNumber,
  getUniqueNumber,
  hexToRgb,
  isBrowser,
  isEdge,
  isFirefox,
  isIE,
  isSafari,
  isTestEnv,
  isTouchDevice,
  kebabCase,
  logWarningOnce,
  nonNullableValueOrThrow,
  parseQueryStringParams,
  recombineTaggedTemplateStr,
  reflow,
  removeLeadingIndent,
  requestAnimationFrame,
  sanitizeHtml,
  setAttributes,
  sleep,
  startsWith,
  throttle,
  trackComponentVersion,
  unsanitizeHtml,
  upperFirst,
  win
};
//# sourceMappingURL=index.mjs.map
